import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { jwtDecode } from 'jwt-decode';

import { GetTeamGroups, type TeamGroup } from '@/apis/teamGroups';
import { useToken } from '@/common/helper';
import { getActiveToken } from '@/common/helper/tokenStorage';
import type { DecodedToken } from '@/common/helper/useToken';
import { groupsForOptions } from '@/common/utils/teamGroups';

// One request shared by every mounted picker, keyed by the identity of the
// token requests send, so a different one (e.g. entering or leaving permission
// preview) never sees another's groups. Pickers revalidate on mount and on
// window focus once the cache is older than this; Settings invalidates it
// directly, and a failed request retries on the next mount or focus.
export const TEAM_GROUPS_TTL_MS = 60 * 1000;

const NO_GROUPS: TeamGroup[] = [];

/**
 * The token requests actually send. A picker's own ``useToken()`` copy can lag:
 * entering or leaving permission preview updates only the caller's copy, and a
 * refresh updates none. Fall back to the copy only when storage has nothing.
 */
const sentToken = (fallback: string): string => getActiveToken() || fallback;

let lastIdentity: { token: string; identity: string } | null = null;
/** ``teamGroupsIdentity`` for the token requests send, memoized per token. */
const sentIdentity = (fallback: string): string => {
  const token = sentToken(fallback);
  if (lastIdentity?.token !== token) {
    lastIdentity = { token, identity: teamGroupsIdentity(token) };
  }
  return lastIdentity.identity;
};

/**
 * Who a token signs in as. Each ``useToken()`` keeps its own copy of the token
 * and a refresh doesn't update them, so pickers mounted before and after a
 * refresh hold different strings for the same identity; keying by the claims
 * lets them share one entry. Requests always send the tab's active token.
 */
export function teamGroupsIdentity(token: string): string {
  try {
    const claims = jwtDecode<Partial<DecodedToken>>(token);
    return JSON.stringify([
      claims.user_id,
      claims.company_id,
      claims.is_admin,
      claims.is_company_owner,
      claims.user_role,
      claims.is_preview,
      claims.preview_mode,
      claims.preview_target_user_id,
      claims.preview_audit_id,
    ]);
  } catch {
    // Not a JWT (tests, malformed storage): the token itself is the identity.
    return token;
  }
}

interface CacheEntry {
  identity: string;
  /** Latest token seen for this identity; used for refetches. */
  token: string;
  groups: TeamGroup[];
  /** When the last successful load finished; 0 means stale or never loaded. */
  loadedAt: number;
  inflight: Promise<void> | null;
  /** Bumped by every invalidation, so a request started before one can't mark
   *  its (possibly old) answer fresh. */
  generation: number;
}

let entry: CacheEntry | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

/**
 * Keep only well-formed groups. An older backend ignores ``view=groups`` and
 * returns the plain teams list; that must read as "no groups", not crash.
 */
export function parseTeamGroups(data: unknown): TeamGroup[] {
  if (!Array.isArray(data)) return [];
  return data.filter(
    (group): group is TeamGroup =>
      !!group &&
      typeof group === 'object' &&
      typeof (group as TeamGroup).id === 'string' &&
      typeof (group as TeamGroup).name === 'string' &&
      Array.isArray((group as TeamGroup).team_ids)
  );
}

/** Fetch groups for ``token`` unless a fresh result or a request exists. */
export function refreshTeamGroups(fallbackToken: string): Promise<void> {
  const token = sentToken(fallbackToken);
  const identity = sentIdentity(fallbackToken);
  if (!entry || entry.identity !== identity) {
    // New identity: drop the previous one's groups before anything renders.
    entry = {
      identity,
      token,
      groups: NO_GROUPS,
      loadedAt: 0,
      inflight: null,
      generation: 0,
    };
    notify();
  }
  const current = entry;
  current.token = token;
  if (current.inflight) return current.inflight;
  if (current.loadedAt && Date.now() - current.loadedAt < TEAM_GROUPS_TTL_MS) {
    return Promise.resolve();
  }
  const generation = current.generation;
  const isCurrent = (): boolean => current.generation === generation;
  current.inflight = GetTeamGroups(token)
    .then((res) => {
      current.groups = parseTeamGroups(res?.data?.data?.data);
      // Invalidated while loading: show this answer, but keep it stale.
      current.loadedAt = isCurrent() ? Date.now() : 0;
    })
    .catch(() => {
      // Keep the last good groups; the next refresh retries.
      current.loadedAt = 0;
    })
    .finally(() => {
      current.inflight = null;
      if (entry !== current) return;
      notify();
      // Fetch the invalidated hierarchy now if a picker still shows it;
      // otherwise the stale cache refetches on the next mount.
      if (!isCurrent() && listeners.size > 0) {
        refreshTeamGroups(token).catch((): void => undefined);
      }
    });
  return current.inflight;
}

/** Mark groups stale (e.g. after a team change in Settings) and refetch if shown. */
export function invalidateTeamGroups(): void {
  if (!entry) return;
  entry.loadedAt = 0;
  entry.generation += 1;
  // An in-flight request refetches when it settles (see refreshTeamGroups).
  if (listeners.size > 0 && !entry.inflight) {
    refreshTeamGroups(entry.token).catch((): void => undefined);
  }
}

/** Test hook: forget the cached groups. */
export function resetTeamGroupsCache(): void {
  entry = null;
  lastIdentity = null;
  notify();
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Team groups for a picker, limited to teams among ``optionIds``.
 * Returns [] when disabled, while loading, or if no request has succeeded.
 */
export function useTeamGroups(
  optionIds: Array<string | number>,
  enabled = true
): TeamGroup[] {
  const { token } = useToken();
  const active = enabled && !!token;

  // Keyed by the identity requests send, so a picker that kept an old token
  // copy (e.g. across a permission-preview switch) never shows another
  // identity's groups; it shows none until its next refresh.
  const getSnapshot = useCallback(
    (): TeamGroup[] =>
      active && token && entry && entry.identity === sentIdentity(token)
        ? entry.groups
        : NO_GROUPS,
    [active, token]
  );
  const groups = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (!active || !token) return;
    const refresh = (): void => {
      // refreshTeamGroups never rejects; this only satisfies the linter.
      refreshTeamGroups(token).catch((): void => undefined);
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
    };
  }, [active, token]);

  const optionKey = optionIds.map(String).join(',');
  return useMemo(
    () =>
      active
        ? groupsForOptions(groups, optionKey ? optionKey.split(',') : [])
        : [],
    [active, groups, optionKey]
  );
}
